import { TemplateElement, html, defineElement } from '@webtides/element-js';
import style from './collapse.css';

/**
 * Collapse component for simple show/hide functionality with animation
 * Use this for conditional visibility (e.g., form fields that appear based on user input)
 * For accordion patterns with headers and toggle icons, use mb-accordion-item instead.
 *
 * @property {boolean} open - Whether the content is visible
 *
 * @example
 * <mb-collapse id="my-content" open="true">
 *   <p>This content can be shown/hidden</p>
 * </mb-collapse>
 *
 * // Programmatic control:
 * document.getElementById('my-content').show();
 * document.getElementById('my-content').hide();
 * document.getElementById('my-content').toggle();
 */
export default class Collapse extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true, propertyOptions: { open: { reflect: true } } });
    }

    properties() {
        return {
            open: false,
        };
    }

    connected() {
        if (!this.open) {
            this._setCollapsed(false);
        }
    }

    watch() {
        return {
            open: (open) => {
                if (open) {
                    this._expand();
                } else {
                    this._collapse();
                }
            },
        };
    }

    /**
     * Show the content with animation
     */
    show() {
        this.open = true;
    }

    /**
     * Hide the content with animation
     */
    hide() {
        this.open = false;
    }

    /**
     * Toggle visibility
     */
    toggle() {
        this.open = !this.open;
    }

    _expand() {
        const content = this.$refs.content;
        if (!content) return;

        // Remove collapsed state to measure height
        content.style.height = '0px';
        content.style.overflow = 'hidden';

        // Force reflow
        void content.offsetHeight;

        // Animate to full height
        content.style.height = `${content.scrollHeight}px`;

        const onTransitionEnd = () => {
            content.style.height = '';
            content.style.overflow = '';
            content.removeEventListener('transitionend', onTransitionEnd);
        };

        content.addEventListener('transitionend', onTransitionEnd);

        // Fallback
        setTimeout(() => {
            if (content.style.height !== '') {
                onTransitionEnd();
            }
        }, 350);
    }

    _collapse() {
        const content = this.$refs.content;
        if (!content) return;

        // Set current height explicitly
        content.style.height = `${content.getBoundingClientRect().height}px`;
        content.style.overflow = 'hidden';

        // Force reflow
        void content.offsetHeight;

        // Animate to zero
        content.style.height = '0px';

        const onTransitionEnd = () => {
            content.style.height = '';
            content.removeEventListener('transitionend', onTransitionEnd);
        };

        content.addEventListener('transitionend', onTransitionEnd);

        // Fallback
        setTimeout(() => {
            if (content.style.height === '0px') {
                onTransitionEnd();
            }
        }, 350);
    }

    _setCollapsed(animate = true) {
        const content = this.$refs.content;
        if (!content) return;

        if (!animate) {
            content.style.height = '0px';
            content.style.overflow = 'hidden';
        }
    }

    template() {
        return html`
            <div ref="content" class="content">
                <slot></slot>
            </div>
        `;
    }
}

defineElement('mb-collapse', Collapse);
